import { Box } from 'features/knowledgeDocumentViewer/components/viewer/types'
import { useCallback, useEffect, useRef, useState } from 'react'

interface SelectionBox {
  startX: number
  startY: number
  endX: number
  endY: number
}

interface UseShiftDragSelectionProps {
  svgRef: React.RefObject<SVGSVGElement | null>
  boxes: Box[]
  onSelectionComplete: (selectedBoxIds: string[]) => void
  imageNaturalDimensions: { w: number; h: number } | null
}

export function useShiftDragSelection({
  svgRef,
  boxes,
  onSelectionComplete,
  imageNaturalDimensions,
}: UseShiftDragSelectionProps) {
  const [isShiftPressed, setIsShiftPressed] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null)
  const isDrawingRef = useRef(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(true)
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false)
        if (isDrawingRef.current) {
          setIsDragging(false)
          setSelectionBox(null)
          isDrawingRef.current = false
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  const convertScreenToImageCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      if (!svgRef.current || !imageNaturalDimensions) {
        return null
      }

      const svg = svgRef.current
      const pt = svg.createSVGPoint()
      pt.x = clientX
      pt.y = clientY

      // Transform the point from screen coordinates to SVG coordinates
      const screenCTM = svg.getScreenCTM()
      if (!screenCTM) return null

      const svgCoords = pt.matrixTransform(screenCTM.inverse())

      return { x: svgCoords.x, y: svgCoords.y }
    },
    [svgRef, imageNaturalDimensions]
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isShiftPressed || !imageNaturalDimensions) return

      const coords = convertScreenToImageCoordinates(e.clientX, e.clientY)
      if (!coords) return

      isDrawingRef.current = true
      setIsDragging(true)
      setSelectionBox({
        startX: coords.x,
        startY: coords.y,
        endX: coords.x,
        endY: coords.y,
      })

      e.preventDefault()
      e.stopPropagation()
    },
    [isShiftPressed, imageNaturalDimensions, convertScreenToImageCoordinates]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDrawingRef.current) return

      const coords = convertScreenToImageCoordinates(e.clientX, e.clientY)
      if (!coords) return

      setSelectionBox((prev) =>
        prev
          ? {
              ...prev,
              endX: coords.x,
              endY: coords.y,
            }
          : null
      )

      e.preventDefault()
      e.stopPropagation()
    },
    [convertScreenToImageCoordinates]
  )

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (!isDrawingRef.current || !selectionBox) return

      isDrawingRef.current = false
      setIsDragging(false)

      const minX = Math.min(selectionBox.startX, selectionBox.endX)
      const maxX = Math.max(selectionBox.startX, selectionBox.endX)
      const minY = Math.min(selectionBox.startY, selectionBox.endY)
      const maxY = Math.max(selectionBox.startY, selectionBox.endY)

      const selectedIds = boxes
        .filter((box) => {
          const boxCenterX = box.x + box.w / 2
          const boxCenterY = box.y + box.h / 2

          return (
            boxCenterX >= minX &&
            boxCenterX <= maxX &&
            boxCenterY >= minY &&
            boxCenterY <= maxY
          )
        })
        .map((box) => box.id)

      if (selectedIds.length > 0) {
        onSelectionComplete(selectedIds)
      }

      setSelectionBox(null)

      e.preventDefault()
      e.stopPropagation()
    },
    [selectionBox, boxes, onSelectionComplete]
  )

  const normalizedSelectionBox = selectionBox
    ? {
        x: Math.min(selectionBox.startX, selectionBox.endX),
        y: Math.min(selectionBox.startY, selectionBox.endY),
        width: Math.abs(selectionBox.endX - selectionBox.startX),
        height: Math.abs(selectionBox.endY - selectionBox.startY),
      }
    : null

  return {
    isShiftPressed,
    isDragging,
    selectionBox: normalizedSelectionBox,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  }
}
